import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const THEME_KEY = "hookora_theme";

const platformOptions = [
  { value: "instagram", key: "instagram", fallback: "Instagram" },
  { value: "tiktok", key: "tiktok", fallback: "TikTok" },
  { value: "youtube", key: "youtube", fallback: "YouTube" },
  { value: "x", key: "x", fallback: "X / Twitter" },
  { value: "facebook", key: "facebook", fallback: "Facebook" },
  { value: "linkedin", key: "linkedin", fallback: "LinkedIn" },
  { value: "general", key: "general", fallback: "General" },
];

const outputTypeOptions = [
  { value: "content_ideas", key: "contentIdeas", fallback: "Content Ideas" },
  { value: "hooks", key: "hooks", fallback: "Hooks" },
  { value: "captions", key: "captions", fallback: "Captions" },
  { value: "script_angles", key: "scriptAngles", fallback: "Script Angles" },
  { value: "carousel_ideas", key: "carouselIdeas", fallback: "Carousel Ideas" },
  { value: "video_concepts", key: "videoConcepts", fallback: "Video Concepts" },
];

const getStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_KEY) || "light";
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return (
            item.title ||
            item.label ||
            item.name ||
            item.text ||
            item.content ||
            JSON.stringify(item)
          );
        }
        return String(item ?? "").trim();
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((line) => line.replace(/^[-*•\d.)\s]+/, "").trim())
      .filter(Boolean);
  }

  return [];
};

const toPrettyLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatObjectBlock = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return "";

  return Object.entries(obj)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        const items = normalizeArray(value);
        if (!items.length) return "";
        return `${toPrettyLabel(key)}:\n${items.map((item) => `- ${item}`).join("\n")}`;
      }

      if (value && typeof value === "object") {
        const nested = Object.entries(value)
          .map(([nestedKey, nestedValue]) => `- ${toPrettyLabel(nestedKey)}: ${nestedValue}`)
          .join("\n");
        return nested ? `${toPrettyLabel(key)}:\n${nested}` : "";
      }

      if (value === undefined || value === null || value === "") return "";
      return `${toPrettyLabel(key)}: ${value}`;
    })
    .filter(Boolean)
    .join("\n\n");
};

const formatResultToText = (result, t) => {
  if (!result) return "";

  if (typeof result === "string") return result;

  const sections = [];

  const summary =
    result.summary ||
    result.overview ||
    result.intro ||
    result.strategy ||
    result.result_summary;

  if (summary) {
    sections.push(`${t("studioTools.trendToContentTool.exportLabels.summary")}:\n${summary}`);
  }

  const trendInsight =
    result.trend_insight ||
    result.trend_analysis ||
    result.trend_summary ||
    result.insight;

  if (trendInsight) {
    sections.push(
      `${t("studioTools.trendToContentTool.exportLabels.trendInsight")}:\n${trendInsight}`
    );
  }

  const ideas = normalizeArray(
    result.ideas ||
      result.content_ideas ||
      result.outputs ||
      result.results ||
      result.posts ||
      result.video_ideas
  );

  if (ideas.length) {
    sections.push(
      `${t("studioTools.trendToContentTool.exportLabels.generatedIdeas")}:\n${ideas
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const hooks = normalizeArray(result.hooks || result.hook_options || result.sample_hooks);
  if (hooks.length) {
    sections.push(
      `${t("studioTools.trendToContentTool.exportLabels.hookOptions")}:\n${hooks
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const captions = normalizeArray(
    result.captions || result.caption_options || result.sample_captions
  );
  if (captions.length) {
    sections.push(
      `${t("studioTools.trendToContentTool.exportLabels.captionOptions")}:\n${captions
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const angles = normalizeArray(
    result.angles || result.content_angles || result.script_angles || result.positioning_angles
  );
  if (angles.length) {
    sections.push(
      `${t("studioTools.trendToContentTool.exportLabels.angles")}:\n${angles
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const recommendations = normalizeArray(
    result.recommendations || result.tips || result.best_practices
  );
  if (recommendations.length) {
    sections.push(
      `${t("studioTools.trendToContentTool.exportLabels.recommendations")}:\n${recommendations
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const otherBlock = formatObjectBlock(
    result.extra || result.metadata || result.breakdown || result.framework
  );
  if (otherBlock) {
    sections.push(otherBlock);
  }

  if (!sections.length) {
    return JSON.stringify(result, null, 2);
  }

  return sections.join("\n\n");
};

const TrendToContentTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const [theme, setTheme] = useState(getStoredTheme());
  const [trend, setTrend] = useState("");
  const [brandContext, setBrandContext] = useState("");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [outputType, setOutputType] = useState("content_ideas");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedPlatform = sharedPlatform || "general";
  const topicValue = sharedTopic || "";

  useEffect(() => {
    const syncTheme = () => setTheme(getStoredTheme());

    syncTheme();
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const styles = useMemo(() => {
    return {
      wrapper: {
        display: "flex",
        flexDirection: "column",
        gap: 18,
        color: isDark ? "#f8fafc" : "#0f172a",
      },
      card: {
        background: isDark
          ? "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.90) 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 20,
        padding: 20,
        boxShadow: isDark
          ? "0 10px 30px rgba(0,0,0,0.22)"
          : "0 10px 30px rgba(15,23,42,0.06)",
      },
      headerRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        alignItems: "flex-start",
        flexWrap: "wrap",
      },
      titleWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 780,
      },
      badge: {
        alignSelf: "flex-start",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(16,185,129,0.16)" : "rgba(5,150,105,0.10)",
        color: isDark ? "#6ee7b7" : "#047857",
        border: `1px solid ${isDark ? "rgba(16,185,129,0.22)" : "rgba(5,150,105,0.16)"}`,
      },
      title: {
        margin: 0,
        fontSize: 22,
        fontWeight: 800,
        letterSpacing: "-0.02em",
      },
      subtitle: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.6,
        color: isDark ? "rgba(226,232,240,0.78)" : "rgba(15,23,42,0.68)",
      },
      formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
        marginTop: 18,
      },
      fieldWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
      },
      fullWidthField: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        gridColumn: "1 / -1",
      },
      label: {
        fontSize: 13,
        fontWeight: 700,
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      input: {
        width: "100%",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: "13px 14px",
        outline: "none",
        fontSize: 14,
        boxSizing: "border-box",
      },
      textarea: {
        width: "100%",
        minHeight: 128,
        resize: "vertical",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: 14,
        outline: "none",
        fontSize: 14,
        lineHeight: 1.55,
        boxSizing: "border-box",
      },
      actionsRow: {
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        marginTop: 18,
      },
      primaryButton: {
        border: "none",
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 800,
        cursor: loading ? "not-allowed" : "pointer",
        background: loading
          ? isDark
            ? "rgba(16,185,129,0.45)"
            : "rgba(5,150,105,0.45)"
          : isDark
          ? "linear-gradient(135deg, #10b981 0%, #14b8a6 100%)"
          : "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
        color: "#ffffff",
        boxShadow: loading
          ? "none"
          : isDark
          ? "0 12px 24px rgba(16,185,129,0.20)"
          : "0 12px 24px rgba(5,150,105,0.16)",
      },
      secondaryButton: {
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(30,41,59,0.82)" : "#ffffff",
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      mutedText: {
        fontSize: 13,
        color: isDark ? "rgba(226,232,240,0.68)" : "rgba(15,23,42,0.62)",
      },
      errorBox: {
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        background: isDark ? "rgba(127,29,29,0.25)" : "rgba(254,226,226,1)",
        border: `1px solid ${isDark ? "rgba(248,113,113,0.22)" : "rgba(248,113,113,0.28)"}`,
        color: isDark ? "#fecaca" : "#991b1b",
        fontSize: 14,
        lineHeight: 1.5,
      },
      resultCard: {
        background: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 18,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      },
      resultHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      },
      resultTitle: {
        margin: 0,
        fontSize: 18,
        fontWeight: 800,
      },
      resultActions: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "center",
      },
      section: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
      },
      sectionTitle: {
        margin: 0,
        fontSize: 14,
        fontWeight: 800,
        color: isDark ? "#f1f5f9" : "#0f172a",
      },
      paragraph: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.7,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        whiteSpace: "pre-wrap",
      },
      list: {
        margin: 0,
        paddingLeft: 18,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        fontSize: 14,
        lineHeight: 1.65,
      },
      chipRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
      },
      chip: {
        padding: "7px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(51,65,85,0.88)" : "#ffffff",
        color: isDark ? "#cbd5e1" : "#334155",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      },
    };
  }, [isDark, loading]);

  const formattedText = useMemo(() => formatResultToText(result, t), [result, t]);

  const ideaList = useMemo(
    () =>
      normalizeArray(
        result?.ideas ||
          result?.content_ideas ||
          result?.outputs ||
          result?.results ||
          result?.posts ||
          result?.video_ideas
      ),
    [result]
  );

  const hookList = useMemo(
    () => normalizeArray(result?.hooks || result?.hook_options || result?.sample_hooks),
    [result]
  );

  const captionList = useMemo(
    () => normalizeArray(result?.captions || result?.caption_options || result?.sample_captions),
    [result]
  );

  const angleList = useMemo(
    () =>
      normalizeArray(
        result?.angles ||
          result?.content_angles ||
          result?.script_angles ||
          result?.positioning_angles
      ),
    [result]
  );

  const recommendationList = useMemo(
    () => normalizeArray(result?.recommendations || result?.tips || result?.best_practices),
    [result]
  );

  const clearResults = () => {
    setResult(null);
    setError("");
    setCopied(false);
  };

  const resetAll = () => {
    setTrend("");
    setBrandContext("");
    setAudience("");
    setGoal("");
    setOutputType("content_ideas");
    setResult(null);
    setCopied(false);
    setError("");
    if (typeof setSharedTopic === "function") setSharedTopic("");
    if (typeof setSharedPlatform === "function") setSharedPlatform("general");
  };

  const copyResult = async () => {
    if (!formattedText) return;

    try {
      await navigator.clipboard.writeText(formattedText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (copyError) {
      setError(t("studioTools.trendToContentTool.messages.copyFailed"));
    }
  };

  const tryPost = async (paths, payload) => {
    let lastError = null;

    for (const path of paths) {
      try {
        const response = await API.post(path, payload);
        return response?.data;
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError;
  };

  const handleGenerate = async () => {
    if (!trend.trim()) {
      setError(t("studioTools.trendToContentTool.messages.trendRequired"));
      return;
    }

    setLoading(true);
    setError("");
    setCopied(false);

    const payload = {
      trend: trend.trim(),
      topic: topicValue.trim(),
      platform: selectedPlatform,
      brand_context: brandContext.trim(),
      target_audience: audience.trim(),
      goal: goal.trim(),
      output_type: outputType,
    };

    try {
      const data = await tryPost(
        [
          "/studio/trend-to-content",
          "/studio/trend_to_content",
          "/studio/trend-content",
          "/studio/trend_content",
        ],
        payload
      );

      const normalized =
        data?.result ||
        data?.analysis ||
        data?.data ||
        data?.trend_to_content ||
        data;

      setResult(normalized);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        t("studioTools.trendToContentTool.messages.generateFailed");
      setError(message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MotionSurface>
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.titleWrap}>
            <span style={styles.badge}>{t("studioTools.trendToContentTool.badge")}</span>
            <h2 style={styles.title}>{t("studioTools.trendToContentTool.title")}</h2>
            <p style={styles.subtitle}>{t("studioTools.trendToContentTool.description")}</p>
          </div>
        </div>

        <div style={styles.formGrid}>
          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.trendToContentTool.fields.topic.label")}</label>
            <input
              type="text"
              value={topicValue}
              onChange={(e) => setSharedTopic?.(e.target.value)}
              placeholder={t("studioTools.trendToContentTool.fields.topic.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.trendToContentTool.fields.topic.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.trendToContentTool.fields.platform.label")}</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSharedPlatform?.(e.target.value)}
              style={styles.input}
            >
              {platformOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`studioTools.trendToContentTool.platformOptions.${option.key}`, option.fallback)}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.trendToContentTool.fields.platform.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.trendToContentTool.fields.outputType.label")}</label>
            <select
              value={outputType}
              onChange={(e) => setOutputType(e.target.value)}
              style={styles.input}
            >
              {outputTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(
                    `studioTools.trendToContentTool.outputTypeOptions.${option.key}`,
                    option.fallback
                  )}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.trendToContentTool.fields.outputType.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.trendToContentTool.fields.goal.label")}</label>
            <input
              type="text"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t("studioTools.trendToContentTool.fields.goal.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.trendToContentTool.fields.goal.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.trendToContentTool.fields.audience.label")}</label>
            <input
              type="text"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder={t("studioTools.trendToContentTool.fields.audience.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.trendToContentTool.fields.audience.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>{t("studioTools.trendToContentTool.fields.trend.label")}</label>
            <textarea
              value={trend}
              onChange={(e) => setTrend(e.target.value)}
              placeholder={t("studioTools.trendToContentTool.fields.trend.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.trendToContentTool.fields.trend.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>{t("studioTools.trendToContentTool.fields.brandContext.label")}</label>
            <textarea
              value={brandContext}
              onChange={(e) => setBrandContext(e.target.value)}
              placeholder={t("studioTools.trendToContentTool.fields.brandContext.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.trendToContentTool.fields.brandContext.hint")} />
          </div>
        </div>

        <ActionRow>
          <AIActionButton isDark={isDark}
            type="button"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading
              ? t("studioTools.trendToContentTool.actions.generating")
              : t("studioTools.trendToContentTool.actions.generate")}
          </AIActionButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={clearResults}
          >
            {t("studioTools.trendToContentTool.actions.clearResults")}
          </UtilityButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={resetAll}
          >
            {t("studioTools.trendToContentTool.actions.reset")}
          </UtilityButton>
        </ActionRow>

        <div style={{ marginTop: 10 }}>
          <span style={styles.mutedText}>{t("studioTools.trendToContentTool.tip")}</span>
        </div>

        {error ? <div style={styles.errorBox}>{error}</div> : null}
      </div>

      {loading ? (
        <ToolResultSkeleton isDark={isDark} count={3} />
      ) : result ? (
        <div style={styles.card}>
          <ResultCard colors={{ border: isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)", cardBgStrong: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)", shadowSoft: isDark ? "0 10px 24px rgba(2,6,23,0.22)" : "0 6px 18px rgba(15,23,42,0.05)" }} index={0}>
            <div style={styles.resultHeader}>
              <h3 style={styles.resultTitle}>{t("studioTools.trendToContentTool.results.title")}</h3>

              <ActionRow style={styles.resultActions}>
                <CopyButton
                  type="button"
                  copied={copied}
                  onClick={copyResult}
                >
                  {copied
                    ? t("studioTools.trendToContentTool.actions.copied")
                    : t("studioTools.trendToContentTool.actions.copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="trend_to_content"
                  title={`Trend To Content${topicValue ? ` - ${topicValue}` : ""}`}
                  topic={topicValue || "Trend To Content"}
                  platform={selectedPlatform}
                  content={formattedText}
                  meta={{
                    trend,
                    brandContext,
                    audience,
                    goal,
                    outputType,
                  }}
                />
              </ActionRow>
            </div>

            <div style={styles.chipRow}>
              <span style={styles.chip}>{toPrettyLabel(outputType)}</span>
              <span style={styles.chip}>
                {t(
                  `studioTools.trendToContentTool.platformOptions.${selectedPlatform}`,
                  platformOptions.find((item) => item.value === selectedPlatform)?.fallback || "General"
                )}
              </span>
              {topicValue ? <span style={styles.chip}>{topicValue}</span> : null}
            </div>

            {result?.summary ||
            result?.overview ||
            result?.intro ||
            result?.strategy ||
            result?.result_summary ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.trendToContentTool.results.summary")}</h4>
                <p style={styles.paragraph}>
                  {result?.summary ||
                    result?.overview ||
                    result?.intro ||
                    result?.strategy ||
                    result?.result_summary}
                </p>
              </div>
            ) : null}

            {result?.trend_insight ||
            result?.trend_analysis ||
            result?.trend_summary ||
            result?.insight ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.trendToContentTool.results.trendInsight")}</h4>
                <p style={styles.paragraph}>
                  {result?.trend_insight ||
                    result?.trend_analysis ||
                    result?.trend_summary ||
                    result?.insight}
                </p>
              </div>
            ) : null}

            {ideaList.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.trendToContentTool.results.generatedIdeas")}</h4>
                <ul style={styles.list}>
                  {ideaList.map((item, index) => (
                    <li key={`idea-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {hookList.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.trendToContentTool.results.hookOptions")}</h4>
                <ul style={styles.list}>
                  {hookList.map((item, index) => (
                    <li key={`hook-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {captionList.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.trendToContentTool.results.captionOptions")}</h4>
                <ul style={styles.list}>
                  {captionList.map((item, index) => (
                    <li key={`caption-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {angleList.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.trendToContentTool.results.angles")}</h4>
                <ul style={styles.list}>
                  {angleList.map((item, index) => (
                    <li key={`angle-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {recommendationList.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.trendToContentTool.results.recommendations")}</h4>
                <ul style={styles.list}>
                  {recommendationList.map((item, index) => (
                    <li key={`recommendation-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {!formattedText ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t("studioTools.trendToContentTool.results.rawResult")}</h4>
                <p style={styles.paragraph}>{JSON.stringify(result, null, 2)}</p>
              </div>
            ) : null}
          </ResultCard>
        </div>
      ) : null}
    </div>
    </MotionSurface>
  );
};

export default TrendToContentTool;